import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Eye, Search, Moon, Sun, Menu, X } from 'lucide-react';
import { useAuthStore } from '@/shared/stores/authStore';
import { useThemeStore } from '@/shared/stores/themeStore';
import { useSearchStore } from '@/shared/stores/searchStore';
import { useScrollProgress } from '@/shared/hooks/useScrollProgress';
import apiClient from '@/shared/services/apiClient';

export default function Header() {
  const { isAuthenticated, username, isAdmin, logout } = useAuthStore();
  const { pathname } = useLocation();
  const { theme, toggle: toggleTheme } = useThemeStore();
  const openSearch = useSearchStore((s) => s.open);
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { headerRef, brandingRef, progressRef } = useScrollProgress();

  const { data: visitData } = useQuery<{ visits: number }>({
    queryKey: ['visits'],
    queryFn: async () => {
      const res = await apiClient.get('/visits');
      return res.data;
    },
  });

  useEffect(() => {
    const visited = sessionStorage.getItem('site_visited');
    if (!visited) {
      sessionStorage.setItem('site_visited', '1');
      apiClient.post('/visits');
    }
  }, []);

  const [prevPathname, setPrevPathname] = useState<string | null>(null);
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setMobileOpen(false);
  }

  const navLink = (to: string, label: string) => {
    const isActive = to === '/' ? pathname === '/' : pathname.startsWith(to);
    return (
      <Link to={to} className={isActive ? 'font-bold' : ''} {...(isActive ? { 'aria-current': 'page' as const } : {})}>
        {label}
      </Link>
    );
  };

  const iconBtnClass = "bg-transparent cursor-pointer flex items-center justify-center";
  const iconBtnStyle = { color: 'var(--color-accent)', border: '1px solid var(--color-border)', width: '28px', height: '28px' };

  return (
    <header
      ref={headerRef}
      className="no-print sticky z-30"
      style={{ backgroundColor: 'var(--color-background)' }}
    >
      <div ref={brandingRef}>
        <div className="container-wide pt-6 pb-3">
          <div className="flex items-center gap-4">
            <Link to="/" className="no-underline hover:no-underline">
              <span className="bg-accent text-background font-bold px-3 py-1 text-sm font-mono whitespace-nowrap">
                Vladyslav Chebotarov
              </span>
            </Link>
            <div className="barcode-pattern h-4 flex-1 opacity-60" />
            <span className="text-xs text-muted whitespace-nowrap font-mono flex items-center gap-1">
              <Eye size={14} />
              {visitData?.visits ?? 0}
            </span>
          </div>
        </div>
      </div>
      <div className="container-wide">
        <nav className="text-sm py-3 relative" role="navigation" aria-label="Main navigation">
          {/* Desktop nav */}
          <div className="hidden md:flex gap-5 items-center">
            {navLink('/', 'Home')}
            {navLink('/blog', 'Blog')}
            {pathname.startsWith('/cv') && navLink('/cv', 'CV')}
            <span className="flex-1" />
            <button
              onClick={() => { navigate('/blog'); setTimeout(openSearch, 50); }}
              className={iconBtnClass}
              aria-label="Search posts"
              title="Search posts"
              style={iconBtnStyle}
            >
              <Search size={14} />
            </button>
            <button
              onClick={toggleTheme}
              className={iconBtnClass}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              style={iconBtnStyle}
            >
              {theme === 'dark' ? <Moon size={14} /> : <Sun size={14} />}
            </button>
            {isAuthenticated ? (
              <>
                {isAdmin && navLink('/admin', 'Admin')}
                {navLink('/settings', 'Settings')}
                <span className="text-muted text-xs">{username}</span>
                <button
                  onClick={logout}
                  className="text-accent hover:underline bg-transparent border-none cursor-pointer font-mono text-sm p-0"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link to="/login">Sign In</Link>
            )}
          </div>

          {/* Mobile nav */}
          <div className="flex md:hidden items-center gap-3">
            {navLink('/', 'Home')}
            {navLink('/blog', 'Blog')}
            {pathname.startsWith('/cv') && navLink('/cv', 'CV')}
            <span className="flex-1" />
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={iconBtnClass}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              style={iconBtnStyle}
            >
              {mobileOpen ? <X size={14} /> : <Menu size={14} />}
            </button>
          </div>

          {/* Mobile dropdown */}
          <div
            className="md:hidden overflow-hidden transition-all duration-200"
            style={{ maxHeight: mobileOpen ? '300px' : '0px' }}
          >
            <div className="pt-4 flex flex-col gap-3 text-sm" style={{ borderTop: '1px solid var(--color-border)' }}>
              <div className="flex gap-3 items-center">
                <button
                  onClick={() => { navigate('/blog'); setTimeout(openSearch, 50); setMobileOpen(false); }}
                  className="bg-transparent cursor-pointer flex items-center gap-2 text-accent font-mono text-sm p-0 border-none"
                  aria-label="Search posts"
                >
                  <Search size={14} />
                  Search
                </button>
              </div>
              <div className="flex gap-3 items-center">
                <button
                  onClick={toggleTheme}
                  className="bg-transparent cursor-pointer flex items-center gap-2 text-accent font-mono text-sm p-0 border-none"
                  aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {theme === 'dark' ? <Moon size={14} /> : <Sun size={14} />}
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </button>
              </div>
              {isAuthenticated ? (
                <>
                  {isAdmin && (
                    <Link to="/admin" className="text-accent">Admin</Link>
                  )}
                  <Link to="/settings">Settings</Link>
                  <span className="text-muted text-xs">{username}</span>
                  <button
                    onClick={logout}
                    className="text-accent hover:underline bg-transparent border-none cursor-pointer font-mono text-sm p-0 text-left"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link to="/login">Sign In</Link>
              )}
            </div>
          </div>
          {/* Separator + Progress bar */}
          <div className="absolute left-0 right-0 bottom-0 h-px" style={{ background: 'var(--color-border)' }}>
            <div ref={progressRef} className="h-full" style={{ background: 'var(--color-accent)', width: '0%', transition: 'width 100ms linear' }} />
          </div>
        </nav>
      </div>
    </header>
  );
}
